import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiTrendingUp, FiSearch, FiDownload, FiPrinter, FiExternalLink } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const num = (x) => Number(x) || 0;
const formatMoney = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (amount === null || amount === undefined || amount === '') return '-';
  return new Intl.NumberFormat('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(amount);
};
const formatDate = (v) => fmtDate(v) || '-';
// Variance = actual − estimate. Positive = over cost = UNFAVOURABLE (red); negative = favourable (green).
const varColor = (v) => (v > 0 ? '#C62828' : v < 0 ? '#2E7D32' : undefined);

const ZERO = { estTot: 0, actTot: 0, totVar: 0, matVar: 0, ohVar: 0, osVar: 0, pctUnfav: 0, count: 0 };

const ProductionVariance = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('production-variance');

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [groupBy, setGroupBy] = useState(() => savedMem?.groupBy ?? 'pro');
  const [itemFilter, setItemFilter] = useState(() => savedMem?.itemFilter ?? 'all');
  const [bomFilter, setBomFilter] = useState(() => savedMem?.bomFilter ?? 'all');
  const [typeFilter, setTypeFilter] = useState(() => savedMem?.typeFilter ?? 'all');

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [rawPros, setRawPros] = useState(() => savedMem?.rawPros ?? []);

  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snapshot = await getDocs(query(collection(db, 'companies', activeCompany.id, 'financialYears'), orderBy('startDate', 'desc')));
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      if (!savedMem) {
        if (activeFinancialYear?.id) setSelectedFYId(activeFinancialYear.id);
        else if (fys.length > 0) setSelectedFYId(fys[0].id);
      }
    } catch (e) { console.error('Error loading financial years:', e); }
  }, [activeCompany, activeFinancialYear, savedMem]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);

  const generate = async () => {
    if (!activeCompany?.id) return;
    if (!selectedFYId) { alert('Please select a financial year'); return; }
    setLoading(true);
    setHasSearched(true);
    try {
      const txRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const snap = await getDocs(query(txRef, where('tranTypeCode', '==', 'PRO')));
      const pros = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => t.financialYearId === selectedFYId)
        .filter(t => t.status === 'Completed') // variance only meaningful once actuals exist
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return (t.branchId || null) === activeBranch.id;
        })
        .filter(t => {
          if (dateFrom && (t.docDate || '') < dateFrom) return false;
          if (dateTo && (t.docDate || '') > dateTo) return false;
          return true;
        });
      setRawPros(pros);
    } catch (e) {
      console.error('Error generating production variance:', e);
      alert('Error generating report. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. Item/BOM/type/group filters are display-only over the
  // already-loaded rows, so we keep the snapshot in sync as the user tweaks them.
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      rawPros,
      hasSearched: true,
      selectedFYId,
      dateFrom,
      dateTo,
      groupBy,
      itemFilter,
      bomFilter,
      typeFilter
    });
  }, [hasSearched, rawPros, selectedFYId, dateFrom, dateTo, groupBy,
      itemFilter, bomFilter, typeFilter, saveMemory]);

  const itemOptions = useMemo(() => {
    const m = new Map();
    rawPros.forEach(p => { if (p.finishedItemId && !m.has(p.finishedItemId)) m.set(p.finishedItemId, p.finishedItemName || p.finishedItemId); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [rawPros]);
  const bomOptions = useMemo(() => [...new Set(rawPros.map(p => p.bomDocNumber).filter(Boolean))].sort(), [rawPros]);
  const typeOptions = useMemo(() => [...new Set(rawPros.map(p => p.productionType).filter(Boolean))].sort(), [rawPros]);

  const filteredPros = useMemo(() => rawPros.filter(p => {
    if (itemFilter !== 'all' && (p.finishedItemId || '') !== itemFilter) return false;
    if (bomFilter !== 'all' && (p.bomDocNumber || '') !== bomFilter) return false;
    if (typeFilter !== 'all' && (p.productionType || '') !== typeFilter) return false;
    return true;
  }), [rawPros, itemFilter, bomFilter, typeFilter]);

  const groups = useMemo(() => {
    const list = filteredPros;
    if (groupBy === 'pro') {
      return list
        .slice()
        .sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''))
        .map(p => ({
          key: p.id, proId: p.id,
          label: p.docNumber || '-',
          sub: `${p.finishedItemName || ''}${p.docDate ? ' · ' + formatDate(p.docDate) : ''}`,
          estMat: num(p.estMaterialCost), actMat: num(p.actualMaterialCost),
          estOh: num(p.estOverheadCost), actOh: num(p.actualOverheadCost),
          estOs: num(p.estOutsourcingCost), actOs: num(p.actualOutsourcingCost),
          estTot: num(p.estTotalCost), actTot: num(p.actualTotalCost),
          plannedQty: num(p.plannedQty), actualQty: num(p.actualQty),
        }));
    }
    const keyFn = groupBy === 'item' ? (p => p.finishedItemId || 'unknown')
      : groupBy === 'bom' ? (p => p.bomDocNumber || '—')
        : (p => (p.docDate || '').slice(0, 7));
    const labelFn = groupBy === 'item' ? (p => p.finishedItemName || 'Unknown')
      : groupBy === 'bom' ? (p => p.bomDocNumber || '—')
        : (p => (p.docDate || '').slice(0, 7) || '—');
    const m = {};
    list.forEach(p => {
      const k = keyFn(p);
      if (!m[k]) m[k] = { key: k, label: labelFn(p), sub: '', count: 0, estMat: 0, actMat: 0, estOh: 0, actOh: 0, estOs: 0, actOs: 0, estTot: 0, actTot: 0, plannedQty: 0, actualQty: 0 };
      const g = m[k];
      g.estMat += num(p.estMaterialCost); g.actMat += num(p.actualMaterialCost);
      g.estOh += num(p.estOverheadCost); g.actOh += num(p.actualOverheadCost);
      g.estOs += num(p.estOutsourcingCost); g.actOs += num(p.actualOutsourcingCost);
      g.estTot += num(p.estTotalCost); g.actTot += num(p.actualTotalCost);
      g.plannedQty += num(p.plannedQty); g.actualQty += num(p.actualQty);
      g.count += 1;
    });
    const arr = Object.values(m);
    if (groupBy === 'period') arr.sort((a, b) => (b.key || '').localeCompare(a.key || ''));
    else arr.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    arr.forEach(g => { g.sub = `${g.count} run${g.count !== 1 ? 's' : ''}`; });
    return arr;
  }, [filteredPros, groupBy]);

  const showUnit = groupBy !== 'period';

  const summary = useMemo(() => {
    if (filteredPros.length === 0) return ZERO;
    const sum = (f) => filteredPros.reduce((s, p) => s + num(p[f]), 0);
    const estTot = sum('estTotalCost');
    const actTot = sum('actualTotalCost');
    const unfav = filteredPros.filter(p => num(p.totalVariance) > 0).length;
    return {
      estTot, actTot,
      totVar: actTot - estTot,
      matVar: sum('actualMaterialCost') - sum('estMaterialCost'),
      ohVar: sum('actualOverheadCost') - sum('estOverheadCost'),
      osVar: sum('actualOutsourcingCost') - sum('estOutsourcingCost'),
      pctUnfav: filteredPros.length ? (unfav / filteredPros.length) * 100 : 0,
      count: filteredPros.length,
    };
  }, [filteredPros]);

  const openPro = (id) => { if (id) navigate('/store/production-order', { state: { viewTransactionId: id } }); };

  const unitEst = (g) => (g.plannedQty > 0 ? g.estTot / g.plannedQty : 0);
  const unitAct = (g) => (g.actualQty > 0 ? g.actTot / g.actualQty : 0);

  const handleExport = () => {
    if (groups.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const groupLabel = { pro: 'Production Order', item: 'Finished Item', bom: 'BOM', period: 'Month' }[groupBy];
    let csv = `Production Cost & Variance Analysis\nCompany: ${activeCompany?.companyName || ''}\nFinancial Year: ${fy?.yearName || fy?.yearCode || ''}\nGrouped by: ${groupLabel}\n`;
    if (dateFrom || dateTo) csv += `Period: ${dateFrom || 'start'} to ${dateTo || 'end'}\n`;
    csv += `\n${groupLabel},Mat Est,Mat Act,Mat Var,OH Est,OH Act,OH Var,OS Est,OS Act,OS Var,Tot Est,Tot Act,Tot Var,Unit Est,Unit Act\n`;
    groups.forEach(g => {
      csv += `"${(g.label || '').replace(/"/g, '""')}",${g.estMat},${g.actMat},${g.actMat - g.estMat},${g.estOh},${g.actOh},${g.actOh - g.estOh},${g.estOs},${g.actOs},${g.actOs - g.estOs},${g.estTot},${g.actTot},${g.actTot - g.estTot},${showUnit ? unitEst(g).toFixed(2) : ''},${showUnit ? unitAct(g).toFixed(2) : ''}\n`;
    });
    csv += `\nTotals,,,${summary.matVar},,,${summary.ohVar},,,${summary.osVar},${summary.estTot},${summary.actTot},${summary.totVar},,\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ProductionVariance.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (groups.length === 0) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const groupLabel = { pro: 'Production Order', item: 'Finished Item', bom: 'BOM', period: 'Month' }[groupBy];
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Production Cost & Variance</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:20px;color:#1A1A1A;font-size:11px}
        h1{color:#800020;font-size:18px;margin-bottom:4px}
        .meta{color:#666;font-size:11px;margin-bottom:12px}
        .kpis{display:flex;flex-wrap:wrap;gap:16px;padding:12px;background:#f9f9f9;border-radius:8px;margin-bottom:12px}
        .kpi{text-align:center}.kpi .l{font-size:10px;color:#666}.kpi .v{font-size:14px;font-weight:600}
        table{width:100%;border-collapse:collapse}
        th{background:#800020;color:#fff;padding:5px;text-align:center;font-size:9px;text-transform:uppercase}
        td{padding:5px;border-bottom:1px solid #eee}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .fav{color:#2E7D32}.unfav{color:#C62828}
        tr.total td{background:#efe6e9;font-weight:700}
        @media print{body{margin:10px}@page{size:landscape}}
      </style></head><body>
      <h1>Production Cost &amp; Variance Analysis</h1>
      <div class="meta">${activeCompany?.companyName || ''} | FY: ${fy?.yearName || fy?.yearCode || ''} | Grouped by: ${groupLabel}${(dateFrom || dateTo) ? ' | Period: ' + (dateFrom || 'start') + ' to ' + (dateTo || 'end') : ''}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Total Est</div><div class="v">${formatMoney(summary.estTot)}</div></div>
        <div class="kpi"><div class="l">Total Actual</div><div class="v">${formatMoney(summary.actTot)}</div></div>
        <div class="kpi"><div class="l">Total Variance</div><div class="v">${formatMoney(summary.totVar)}</div></div>
        <div class="kpi"><div class="l">Material Var</div><div class="v">${formatMoney(summary.matVar)}</div></div>
        <div class="kpi"><div class="l">Overhead Var</div><div class="v">${formatMoney(summary.ohVar)}</div></div>
        <div class="kpi"><div class="l">Outsourcing Var</div><div class="v">${formatMoney(summary.osVar)}</div></div>
        <div class="kpi"><div class="l">% Unfavourable</div><div class="v">${summary.pctUnfav.toFixed(0)}%</div></div>
      </div>
      <table>
        <thead>
          <tr>
            <th rowspan="2" style="text-align:left">${groupLabel}</th>
            <th colspan="3">Material</th><th colspan="3">Overhead</th><th colspan="3">Outsourcing</th><th colspan="3">Total</th><th colspan="2">Cost / Unit</th>
          </tr>
          <tr>
            <th>Est</th><th>Act</th><th>Var</th><th>Est</th><th>Act</th><th>Var</th><th>Est</th><th>Act</th><th>Var</th><th>Est</th><th>Act</th><th>Var</th><th>Est</th><th>Act</th>
          </tr>
        </thead>
        <tbody>
          ${groups.map(g => {
            const cell = (v) => `<td class="amount ${v > 0 ? 'unfav' : v < 0 ? 'fav' : ''}">${formatMoney(v)}</td>`;
            return `<tr>
              <td>${g.label}${g.sub ? ' <span style="color:#999">(' + g.sub + ')</span>' : ''}</td>
              <td class="amount">${formatMoney(g.estMat)}</td><td class="amount">${formatMoney(g.actMat)}</td>${cell(g.actMat - g.estMat)}
              <td class="amount">${formatMoney(g.estOh)}</td><td class="amount">${formatMoney(g.actOh)}</td>${cell(g.actOh - g.estOh)}
              <td class="amount">${formatMoney(g.estOs)}</td><td class="amount">${formatMoney(g.actOs)}</td>${cell(g.actOs - g.estOs)}
              <td class="amount">${formatMoney(g.estTot)}</td><td class="amount">${formatMoney(g.actTot)}</td>${cell(g.actTot - g.estTot)}
              <td class="amount">${showUnit ? formatMoney(unitEst(g)) : '-'}</td><td class="amount">${showUnit ? formatMoney(unitAct(g)) : '-'}</td>
            </tr>`;
          }).join('')}
          <tr class="total">
            <td>Totals</td>
            <td colspan="2"></td><td class="amount">${formatMoney(summary.matVar)}</td>
            <td colspan="2"></td><td class="amount">${formatMoney(summary.ohVar)}</td>
            <td colspan="2"></td><td class="amount">${formatMoney(summary.osVar)}</td>
            <td class="amount">${formatMoney(summary.estTot)}</td><td class="amount">${formatMoney(summary.actTot)}</td><td class="amount">${formatMoney(summary.totVar)}</td>
            <td colspan="2"></td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  const groupHeader = { pro: 'Production Order', item: 'Finished Item', bom: 'BOM', period: 'Month' }[groupBy];

  return (
    <PageLayout pageTitle="Production Cost & Variance" breadcrumb="Reports / Production Reports / Cost & Variance">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year *</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Group By</Label>
            <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="pro">Production Order</option>
              <option value="item">Finished Item</option>
              <option value="bom">BOM</option>
              <option value="period">Month</option>
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Finished Item</Label>
            <Select value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
              <option value="all">All Items</option>
              {itemOptions.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>BOM</Label>
            <Select value={bomFilter} onChange={(e) => setBomFilter(e.target.value)}>
              <option value="all">All BOMs</option>
              {bomOptions.map(b => <option key={b} value={b}>{b}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Production Type</Label>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FilterGroup>
        </FiltersGrid>
        <ButtonGroup>
          <Button onClick={handleExport} disabled={groups.length === 0}><FiDownload /> Export CSV</Button>
          <Button onClick={handlePrint} disabled={groups.length === 0}><FiPrinter /> Print</Button>
          <Button primary onClick={generate} disabled={loading}><FiSearch /> {loading ? 'Loading...' : 'Generate Report'}</Button>
        </ButtonGroup>
      </FiltersCard>

      {hasSearched && !loading && filteredPros.length > 0 && (
        <KpiGrid>
          <Kpi><KpiLabel>Total Est Cost</KpiLabel><KpiValue>{formatMoney(summary.estTot)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Total Actual Cost</KpiLabel><KpiValue>{formatMoney(summary.actTot)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Total Variance</KpiLabel><KpiValue style={{ color: varColor(summary.totVar) }}>{formatMoney(summary.totVar)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Material Var</KpiLabel><KpiValue style={{ color: varColor(summary.matVar) }}>{formatMoney(summary.matVar)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Overhead Var</KpiLabel><KpiValue style={{ color: varColor(summary.ohVar) }}>{formatMoney(summary.ohVar)}</KpiValue></Kpi>
          <Kpi><KpiLabel>Outsourcing Var</KpiLabel><KpiValue style={{ color: varColor(summary.osVar) }}>{formatMoney(summary.osVar)}</KpiValue></Kpi>
          <Kpi><KpiLabel>% Unfavourable</KpiLabel><KpiValue>{summary.pctUnfav.toFixed(0)}%</KpiValue></Kpi>
        </KpiGrid>
      )}

      <ContentCard>
        {!hasSearched ? (
          <EmptyState>
            <EmptyIcon><FiTrendingUp /></EmptyIcon>
            <EmptyText>Select a financial year and generate to analyse production cost variances</EmptyText>
            <Hint>Covers Completed production orders (estimated vs actual cost).</Hint>
          </EmptyState>
        ) : loading ? (
          <EmptyState><EmptyText>Loading...</EmptyText></EmptyState>
        ) : filteredPros.length === 0 ? (
          <EmptyState>
            <EmptyIcon><FiTrendingUp /></EmptyIcon>
            <EmptyText>No completed production orders match the selected filters</EmptyText>
          </EmptyState>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th rowSpan={2}>{groupHeader}</Th>
                  <Th colSpan={3} align="center" $grp>Material</Th>
                  <Th colSpan={3} align="center" $grp>Overhead</Th>
                  <Th colSpan={3} align="center" $grp>Outsourcing</Th>
                  <Th colSpan={3} align="center" $grp>Total</Th>
                  <Th colSpan={2} align="center" $grp>Cost / Unit</Th>
                </Tr>
                <Tr>
                  <Th align="right">Est</Th><Th align="right">Act</Th><Th align="right">Var</Th>
                  <Th align="right">Est</Th><Th align="right">Act</Th><Th align="right">Var</Th>
                  <Th align="right">Est</Th><Th align="right">Act</Th><Th align="right">Var</Th>
                  <Th align="right">Est</Th><Th align="right">Act</Th><Th align="right">Var</Th>
                  <Th align="right">Est</Th><Th align="right">Act</Th>
                </Tr>
              </Thead>
              <Tbody>
                {groups.map(g => {
                  const matV = g.actMat - g.estMat, ohV = g.actOh - g.estOh, osV = g.actOs - g.estOs, totV = g.actTot - g.estTot;
                  return (
                    <Tr key={g.key}>
                      <Td>
                        {groupBy === 'pro' && g.proId ? (
                          <DocNumber onClick={() => openPro(g.proId)} title="Open production order">{g.label} <FiExternalLink size={11} /></DocNumber>
                        ) : g.label}
                        {g.sub ? <Sub>{g.sub}</Sub> : null}
                      </Td>
                      <AmountCell align="right">{formatMoney(g.estMat)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.actMat)}</AmountCell>
                      <AmountCell align="right" style={{ color: varColor(matV) }}>{formatMoney(matV)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.estOh)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.actOh)}</AmountCell>
                      <AmountCell align="right" style={{ color: varColor(ohV) }}>{formatMoney(ohV)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.estOs)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.actOs)}</AmountCell>
                      <AmountCell align="right" style={{ color: varColor(osV) }}>{formatMoney(osV)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.estTot)}</AmountCell>
                      <AmountCell align="right">{formatMoney(g.actTot)}</AmountCell>
                      <AmountCell align="right" style={{ color: varColor(totV), fontWeight: 600 }}>{formatMoney(totV)}</AmountCell>
                      <AmountCell align="right">{showUnit ? formatMoney(unitEst(g)) : '-'}</AmountCell>
                      <AmountCell align="right">{showUnit ? formatMoney(unitAct(g)) : '-'}</AmountCell>
                    </Tr>
                  );
                })}
                <Tr $total>
                  <Td style={{ fontWeight: 700 }}>Totals</Td>
                  <AmountCell align="right" colSpan={2} />
                  <AmountCell align="right" style={{ color: varColor(summary.matVar), fontWeight: 700 }}>{formatMoney(summary.matVar)}</AmountCell>
                  <AmountCell align="right" colSpan={2} />
                  <AmountCell align="right" style={{ color: varColor(summary.ohVar), fontWeight: 700 }}>{formatMoney(summary.ohVar)}</AmountCell>
                  <AmountCell align="right" colSpan={2} />
                  <AmountCell align="right" style={{ color: varColor(summary.osVar), fontWeight: 700 }}>{formatMoney(summary.osVar)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatMoney(summary.estTot)}</AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>{formatMoney(summary.actTot)}</AmountCell>
                  <AmountCell align="right" style={{ color: varColor(summary.totVar), fontWeight: 700 }}>{formatMoney(summary.totVar)}</AmountCell>
                  <AmountCell align="right" colSpan={2} />
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </ContentCard>
    </PageLayout>
  );
};

// ---- Styled Components ----
const FiltersCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; padding: 20px; margin-bottom: 20px;
`;
const FiltersGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 16px;
`;
const FilterGroup = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;
const Label = styled.label`
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase;
`;
const Select = styled.select`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary}; background: white; cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]}; border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const ButtonGroup = styled.div`
  display: flex; gap: 12px; justify-content: flex-end;
`;
const Button = styled.button`
  padding: 10px 20px; border: none; border-radius: ${colors.borderRadius.md}; font-size: 14px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.2s;
  ${props => props.primary ? `
    background: ${colors.primary.main}; color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: ${colors.neutral[100]}; color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[200]}; }
  `}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const KpiGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px; margin-bottom: 20px;
`;
const Kpi = styled.div`
  background: white; padding: 16px; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm};
`;
const KpiLabel = styled.div`
  font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 6px;
`;
const KpiValue = styled.div`
  font-size: 19px; font-weight: 600; color: ${colors.text.primary};
`;
const ContentCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; overflow: hidden;
`;
const TableContainer = styled.div`
  overflow-x: auto;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; min-width: 1100px;
`;
const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;
const Th = styled.th`
  padding: 9px 10px; text-align: ${props => props.align || 'left'}; font-size: 11px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; letter-spacing: 0.4px; border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap;
  ${props => props.$grp && `background: ${colors.primary.main}0D; border-left: 1px solid ${colors.neutral[200]};`}
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
  ${props => props.$total && `background: ${colors.primary.main}0D; font-weight: 600;`}
`;
const Td = styled.td`
  padding: 9px 10px; font-size: 13px; color: ${colors.text.primary}; text-align: ${props => props.align || 'left'}; white-space: nowrap;
`;
const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace; font-size: 12px; text-align: right;
`;
const Sub = styled.div`
  font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px;
`;
const DocNumber = styled.span`
  font-weight: 500; color: ${colors.primary.main}; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;
  &:hover { text-decoration: underline; }
  svg { opacity: 0.6; }
`;
const EmptyState = styled.div`
  text-align: center; padding: 60px 20px; color: ${colors.text.secondary};
`;
const EmptyIcon = styled.div`
  font-size: 48px; color: ${colors.neutral[300]}; margin-bottom: 16px;
`;
const EmptyText = styled.p`
  font-size: 16px; margin-bottom: 8px;
`;
const Hint = styled.p`
  font-size: 13px; color: ${colors.text.tertiary};
`;

export default ProductionVariance;
